<template>
	<v-card style="width: 100%; height: 100%; min-height: 250px">
		<v-card-title class="py-2 py-lg-4">
			<v-row no-gutters>
				<v-col cols="6" class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
					{{ $t('allergies') }}
				</v-col>
				<v-spacer />
				<v-col cols="2" class="text-right">
					<ActionButton
						icon="mdi-file-delimited"
						tooltip="download-csv"
						:maxSize="25"
						:minSize="20"
						@clicked="downloadCSV()"
						:busy="downloading" />
					<v-icon
						color="primary"
						:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
						@click="synchronizeAddDrawers('ccmAllergyAdd')">
						mdi-plus-circle
					</v-icon>
				</v-col>
			</v-row>
		</v-card-title>
		<v-card-text :style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
			<v-data-table
				:items="allergies"
				:headers="headers"
				:items-per-page="-1"
				style="overflow-y: auto; height: 100%"
				:height="'calc(100% - 59px)'"
				fixed-header
				dense
				sort-by="noteDate"
				sort-desc
				class="data-table-outline"
				:footer-props="{ 'items-per-page-text': '' }">
				<template v-slot:item="{ item, index }">
					<tr :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item)">
						<td class="text-left px-1">
							{{ item.allergen }}
						</td>
						<td class="text-left px-1">
							{{ item.reaction }}
						</td>
						<td class="text-left px-1">
							{{ item.severity }}
						</td>
						<td class="text-left px-1">
							{{ formatTimestamp(item.recordedAt) }}
						</td>
						<td class="text-left px-1">
							{{ formatDuration(item.timeSpentSeconds) }}
						</td>
						<td class="px-1 text-right">
							<ActionButton
								v-if="item.ccmRecordId === currentRecord.id"
								icon="mdi-delete"
								tooltip="delete"
								:maxSize="25"
								:minSize="20"
								@clicked="
									deleteId = item.id;
									showDeleteDialog = true;
								"
								:busy="deletingIds.includes(item.id)" />
							<v-tooltip left v-if="item.ccmRecordId === currentRecord.id">
								<template v-slot:activator="{ on }">
									<v-icon
										color="primary"
										:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
										v-on="on"
										@click.stop="selectAllergyToEdit(item)">
										mdi-pencil
									</v-icon>
								</template>
								<span>{{ $t('edit') }}</span>
							</v-tooltip>
						</td>
					</tr>
				</template>
				<template v-slot:[`footer.prepend`]>
					<v-row dense class="ma-0">
						<v-col class="text-left">
							<p class="ma-0">
								<v-tooltip top>
									<template v-slot:activator="{ on }">
										<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
									</template>
									{{ $t('need-to-select-the-correct-record') }}
								</v-tooltip>
							</p>
						</v-col>
					</v-row>
				</template>
			</v-data-table>
		</v-card-text>
		<CCMAllergyViewDialog
			:showDialog="showViewDialog"
			v-if="showViewDialog && selectedAllergy"
			:selectedAllergy="selectedAllergy"
			@closeDialog="showViewDialog = false" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingIds.push(deleteId);
				deleteAllergy(deleteId);
				deleteId = -1;
			" />
	</v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getRowsForDataTable,
	formatDuration,
	formatTimestamp,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import CCMAllergyViewDialog from '../../../dialogs/CCMAllergyViewDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMAllergy',
	components: { CCMAllergyViewDialog, DeleteDialog, ActionButton },

	data () {
		return {
			selectedAllergy: null,
			deletingIds: [],
			deleteId: -1,
			showDeleteDialog: false,
			showViewDialog: false,
			downloading: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			allergies: (state) => state.ccmAllergy.ccmAllergyByPatientId,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			diagnoses: (state) => state.ccmDiagnoses.ccmDiagnosesByPatientId,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headers () {
			return [
				{
					text: this.$t('allergen'),
					sortable: false,
					value: 'allergen',
					class: 'px-1',
				},
				{
					text: this.$t('reaction'),
					sortable: false,
					value: 'reaction',
					class: 'px-1',
				},
				{
					text: this.$t('severity'),
					sortable: false,
					value: 'severity',
					class: 'px-1',
				},
				{
					text: this.$t('recorded-at'),
					sortable: false,
					value: 'recordedAt',
					class: 'px-1',
				},
				{
					text: this.$t('time-spent-header'),
					sortable: false,
					value: 'timeSpentSeconds',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-2',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getAllergyByPatientId: 'ccmAllergy/getCCMAllergyByPatientId',
			getAllergyById: 'ccmAllergy/getCCMAllergy',
			deleteAllergyById: 'ccmAllergy/deleteCCMAllergy',
		}),

		getRowsForDataTable,
		formatDuration,
		formatTimestamp,
		downloadCSVWithKeys,
		getFullName,

		async deleteAllergy (id) {
			await this.deleteAllergyById(id);
			await this.getAllergyByPatientId(this.detailedUser.userData.id);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},

		openViewDialog (allergy) {
			this.selectedAllergy = allergy;
			this.showViewDialog = true;
		},

		synchronizeAddDrawers (drawer) {
			this.$store.commit('SET_DETAILS_DRAWER', {
				detailsDrawer: true,
				page: drawer,
			});
		},

		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},

		async selectAllergyToEdit (item) {
			await this.$store.commit('ccmAllergy/STORE_CCM_ALLERGY', item);
			await this.getAllergyById(item.id);
			this.synchronizeEditDrawers('ccmAllergyEdit');
		},
		async downloadCSV () {
			this.downloading = true;
			await downloadCSVWithKeys(this.allergies, 'Allergies_for_' + getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: ['allergen', 'reaction', 'severity', 'notes', 'performedBy', 'performedByRole'],
				timestamps: ['recordedAt'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloading = false;
		},
	},
	async mounted () {
		await this.getAllergyByPatientId(this.detailedUser.userData.id);
	},
};
</script>
